import React, { useMemo, useState } from 'react';
import type { ClCampaign, ClFilter, ClHealth, ClLpPhase } from '../../growth-campaign-list/types';
import { CL_CAMPAIGNS, CL_FILTERS } from '../../growth-campaign-list/mockData';
import {
  CL_HEALTH_LABELS,
  CL_LP_PHASE_LABELS,
  GROWTH_CAMPAIGN_LIST_META,
} from '../../growth-campaign-list/config';
import { Icon } from '../Icon/Icon';
import { Segmented } from '../Segmented/Segmented';
import './growth-campaign-list.css';

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const HEALTH_TONE: Record<ClHealth, { accent: string; tint: string }> = {
  optimal: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  learning: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  attention: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  paused: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const LP_PHASE_TONE: Record<ClLpPhase, { accent: string; tint: string }> = {
  exploration: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  validation: { accent: '#7A5CFF', tint: 'rgba(122,79,214,0.13)' },
  exploitation: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  converged: { accent: 'var(--success)', tint: 'var(--success-tint)' },
};

function ClCard({
  children,
  className = '',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
      style={style}
    >
      {children}
    </div>
  );
}

function ClStatTile({
  label,
  value,
  sub,
  subTone,
  valueTone,
}: {
  label: string;
  value: string;
  sub: string;
  subTone?: 'success' | 'neutral';
  valueTone?: 'success' | 'ink';
}) {
  return (
    <ClCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{label}</div>
      <div
        className="mt-2 font-[var(--font-display)] text-[26px] font-extrabold leading-none tracking-[-0.04em]"
        style={{ color: valueTone === 'success' ? 'var(--success)' : 'var(--ink-0)' }}
      >
        {value}
      </div>
      <div
        className="mt-2 text-[12.5px]"
        style={{ color: subTone === 'success' ? 'var(--success)' : 'var(--ink-3)' }}
      >
        {sub}
      </div>
    </ClCard>
  );
}

function ClToggle({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={active}
      className="relative h-5 w-9 shrink-0 cursor-pointer rounded-[10px] border-0 transition-colors duration-200"
      style={{ background: active ? 'var(--success)' : 'var(--ink-4)' }}
    >
      <span
        className="absolute top-0.5 h-4 w-4 rounded-full bg-[var(--cream-1)] shadow-[var(--sh-1)] transition-[left] duration-200"
        style={{ left: active ? 18 : 2 }}
      />
    </button>
  );
}

function ClBudgetBar({ spend, budget }: { spend: number; budget: number }) {
  const pct = budget > 0 ? Math.min((spend / budget) * 100, 100) : 0;
  const color =
    pct > 90 ? 'var(--terracotta)' : pct > 70 ? 'var(--gold)' : 'var(--success)';
  return (
    <div className="w-[100px] shrink-0">
      <div className="mb-1 flex justify-between font-[var(--font-mono)] text-[11px]">
        <span className="text-[var(--ink-2)]">${spend}</span>
        <span className="text-[var(--ink-4)]">${budget}</span>
      </div>
      <div className="h-1 overflow-hidden rounded-[var(--r-pill)] bg-[var(--surface-mute)]">
        <div className="h-full rounded-[var(--r-pill)]" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function ClHealthBadge({ health }: { health: ClHealth }) {
  const t = HEALTH_TONE[health];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-[var(--r-pill)] px-2.5 py-1 text-[11px] font-semibold"
      style={{
        background: t.tint,
        color: t.accent,
        border: `1px solid color-mix(in srgb, ${t.accent} 30%, transparent)`,
      }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: t.accent }} />
      {CL_HEALTH_LABELS[health]}
    </span>
  );
}

function ClLpPhaseBadge({ phase }: { phase: ClLpPhase | null }) {
  if (!phase) return null;
  const t = LP_PHASE_TONE[phase];
  return (
    <span
      className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-[6px] px-2 py-[3px] text-[10.5px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
      title={`Landing page: ${CL_LP_PHASE_LABELS[phase]}`}
    >
      <Icon kind="upload" size={9} stroke={2.4} />
      LP: {CL_LP_PHASE_LABELS[phase]}
    </span>
  );
}

function ClMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: string;
}) {
  return (
    <div className="text-center">
      <div className="text-[10px] font-semibold uppercase tracking-[0.03em] text-[var(--ink-4)]">
        {label}
      </div>
      <div
        className="font-[var(--font-display)] text-[18px] font-bold"
        style={{ color: tone }}
      >
        {value}
      </div>
    </div>
  );
}

function ClCampaignRow({
  campaign,
  onToggle,
  onAdjustBudget,
  onViewLeads,
  onViewReport,
}: {
  campaign: ClCampaign;
  onToggle: () => void;
  onAdjustBudget?: (id: number) => void;
  onViewLeads?: (id: number) => void;
  onViewReport?: (id: number) => void;
}) {
  const c = campaign;
  const alertTint =
    c.alertType === 'success'
      ? 'var(--success-tint)'
      : c.alertType === 'warning'
        ? 'var(--gold-tint)'
        : 'var(--surface-mute)';
  const alertAccent =
    c.alertType === 'success'
      ? 'var(--success)'
      : c.alertType === 'warning'
        ? 'var(--gold)'
        : 'var(--ink-3)';

  return (
    <ClCard
      className="overflow-hidden transition-opacity duration-200"
      style={{
        opacity: c.active ? 1 : 0.55,
        borderColor: c.health === 'attention' ? 'color-mix(in srgb, var(--gold) 40%, transparent)' : undefined,
      }}
    >
      <div className="cl-row flex items-center gap-4 px-6 py-5">
        <ClToggle active={c.active} onToggle={onToggle} />
        <span className="text-[24px] leading-none">{c.icon}</span>
        <div className="min-w-[200px]">
          <div className="text-[15px] font-semibold text-[var(--ink-0)]">{c.name}</div>
          <div className="mt-0.5 text-[12px] text-[var(--ink-4)]">{c.target}</div>
        </div>

        <ClBudgetBar spend={c.spend} budget={c.budget} />

        <div className="cl-metrics flex flex-1 justify-center gap-5">
          <ClMetric label="Leads" value={c.leads ? String(c.leads) : '—'} tone="var(--ink-1)" />
          <ClMetric label="Booked" value={c.booked ? String(c.booked) : '—'} tone="var(--success)" />
          <ClMetric
            label="No-Shows"
            value={c.active ? String(c.noShows) : '—'}
            tone={c.noShows > 0 ? 'var(--terracotta)' : 'var(--ink-4)'}
          />
          <ClMetric
            label="Revenue"
            value={c.revenue > 0 ? `$${(c.revenue / 1000).toFixed(1)}K` : '—'}
            tone="var(--success)"
          />
          <ClMetric
            label="Cost/Patient"
            value={c.costPerPatient > 0 ? `$${c.costPerPatient}` : '—'}
            tone={
              c.costPerPatient > 100
                ? 'var(--gold)'
                : c.costPerPatient > 0
                  ? 'var(--success)'
                  : 'var(--ink-4)'
            }
          />
          <ClMetric
            label="ROI"
            value={c.roi > 0 ? `${c.roi}x` : '—'}
            tone={c.roi > 20 ? 'var(--success)' : c.roi > 0 ? 'var(--ink-1)' : 'var(--ink-4)'}
          />
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <ClLpPhaseBadge phase={c.lpPhase} />
          <ClHealthBadge health={c.health} />
          {!c.active ? (
            <button
              type="button"
              onClick={() => onViewReport?.(c.id)}
              className="cursor-pointer rounded-[6px] border-0 bg-transparent px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]"
            >
              Report →
            </button>
          ) : null}
        </div>
      </div>

      {c.active ? (
        <div
          className="cl-alert flex items-center gap-2.5 border-t border-[var(--cream-2)] px-6 py-2.5"
          style={{ background: alertTint }}
        >
          <Icon kind="bolt" size={14} stroke={2} className="shrink-0" style={{ color: alertAccent }} />
          <span className="flex-1 text-[12.5px] leading-[1.5] text-[var(--ink-2)]">{c.alert}</span>
          <div className="flex shrink-0 gap-1.5">
            <button
              type="button"
              onClick={() => onAdjustBudget?.(c.id)}
              className="cursor-pointer rounded-[6px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-2)]"
            >
              Adjust Budget
            </button>
            <button
              type="button"
              onClick={() => onViewLeads?.(c.id)}
              className="cursor-pointer rounded-[6px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-2)]"
            >
              View Leads
            </button>
            <button
              type="button"
              onClick={() => onViewReport?.(c.id)}
              className="cursor-pointer rounded-[6px] border-0 bg-transparent px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]"
            >
              Report →
            </button>
          </div>
        </div>
      ) : null}
    </ClCard>
  );
}

export interface ClHeaderStats {
  totalSpend: number;
  totalLeads: number;
  totalBooked: number;
  totalNoShows: number;
  totalRevenue: number;
  avgCostPerPatient: number;
  overallRoi: string;
  noShowRate: number;
  spendDeltaLabel?: string;
  roiDeltaLabel?: string;
}

export interface CampaignListViewProps {
  items?: ClCampaign[];
  headerStats?: ClHeaderStats;
  loading?: boolean;
  onToggleActive?: (id: number, nextActive: boolean) => void;
  onNewCampaign?: () => void;
  onAdjustBudget?: (id: number) => void;
  onViewLeads?: (id: number) => void;
  onViewReport?: (id: number) => void;
}

export function CampaignListView({
  items: itemsProp,
  headerStats,
  loading = false,
  onToggleActive,
  onNewCampaign,
  onAdjustBudget,
  onViewLeads,
  onViewReport,
}: CampaignListViewProps = {}) {
  const meta = GROWTH_CAMPAIGN_LIST_META;
  const controlled = itemsProp !== undefined;
  const [campaigns, setCampaigns] = useState<ClCampaign[]>(() =>
    controlled ? (itemsProp ?? []) : CL_CAMPAIGNS,
  );
  const [filter, setFilter] = useState<ClFilter>('all');

  React.useEffect(() => {
    if (controlled) setCampaigns(itemsProp ?? []);
  }, [controlled, itemsProp]);

  const toggleActive = (id: number) => {
    const current = campaigns.find((c) => c.id === id);
    if (!current) return;
    const nextActive = !current.active;
    if (onToggleActive) {
      onToggleActive(id, nextActive);
      return;
    }
    setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, active: nextActive } : c)));
  };

  const totals = useMemo(() => {
    if (headerStats) {
      return headerStats;
    }
    const active = campaigns.filter((c) => c.active);
    const totalSpend = active.reduce((a, c) => a + c.spend, 0);
    const totalBooked = active.reduce((a, c) => a + c.booked, 0);
    const totalRevenue = active.reduce((a, c) => a + c.revenue, 0);
    const totalLeads = active.reduce((a, c) => a + c.leads, 0);
    const totalNoShows = active.reduce((a, c) => a + c.noShows, 0);
    const avgCostPerPatient = totalBooked > 0 ? Math.round(totalSpend / totalBooked) : 0;
    const overallRoi = totalSpend > 0 ? (totalRevenue / totalSpend).toFixed(1) : '0';
    const noShowRate = totalBooked > 0 ? Math.round((totalNoShows / totalBooked) * 100) : 0;
    return {
      totalSpend,
      totalBooked,
      totalRevenue,
      totalLeads,
      totalNoShows,
      avgCostPerPatient,
      overallRoi,
      noShowRate,
    };
  }, [campaigns, headerStats]);

  const filters = useMemo(
    () =>
      CL_FILTERS.map((f) => {
        const count =
          f.id === 'all'
            ? campaigns.length
            : f.id === 'active'
              ? campaigns.filter((c) => c.active).length
              : f.id === 'attention'
                ? campaigns.filter((c) => c.health === 'attention').length
                : campaigns.filter((c) => !c.active).length;
        return { value: f.id, label: `${f.label} (${count})` };
      }),
    [campaigns],
  );

  const rows = useMemo(
    () =>
      campaigns.filter((c) => {
        if (filter === 'active') return c.active;
        if (filter === 'paused') return !c.active;
        if (filter === 'attention') return c.health === 'attention';
        return true;
      }),
    [campaigns, filter],
  );

  return (
    <div className="growth-campaign-list-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px] flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-[720px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="bolt" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            {meta.description}
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={onNewCampaign}
          className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-4 text-sm font-semibold text-[var(--cream-1)] shadow-[var(--sh-1)]"
          style={{ background: 'var(--success)' }}
        >
          <Icon kind="plus" size={16} stroke={2.4} />
          New Campaign
        </button>
      </div>

      <div className="cl-kpi-grid mb-3 grid gap-3" style={{ gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' }}>
        <ClStatTile
          label="Total Ad Spend"
          value={`$${(totals.totalSpend / 1000).toFixed(1)}K`}
          sub="this month"
        />
        <ClStatTile label="Leads Captured" value={String(totals.totalLeads)} sub="calls + forms" />
        <ClStatTile
          label="Patients Booked"
          value={String(totals.totalBooked)}
          sub={`${totals.totalNoShows} no-shows (${totals.noShowRate}%)`}
          valueTone="success"
        />
        <ClStatTile
          label="Revenue Generated"
          value={`$${(totals.totalRevenue / 1000).toFixed(1)}K`}
          sub="from booked appts"
          valueTone="success"
        />
        <ClStatTile
          label="Cost Per Patient"
          value={`$${totals.avgCostPerPatient}`}
          sub={totals.spendDeltaLabel ?? '↓ $6 vs last month'}
          subTone="success"
        />
        <ClStatTile
          label="Return on Every $1"
          value={`$${totals.overallRoi}`}
          sub={totals.roiDeltaLabel ?? '↑ from $38 last month'}
          subTone="success"
          valueTone="success"
        />
      </div>

      <div className="mb-7 flex flex-wrap items-center gap-2.5 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] px-[18px] py-3">
        <Icon kind="dollar" size={18} stroke={2} className="shrink-0 text-[var(--success)]" />
        <span className="text-[14px] font-medium text-[var(--success)]">
          You spent{' '}
          <strong className="text-[var(--ink-1)]">${(totals.totalSpend / 1000).toFixed(1)}K</strong> on
          ads this month and generated{' '}
          <strong className="text-[var(--ink-1)]">${(totals.totalRevenue / 1000).toFixed(1)}K</strong>{' '}
          in booked appointments — a <strong className="text-[var(--ink-1)]">{totals.overallRoi}x return</strong>.
        </span>
      </div>

      <div className="mb-5">
        <Segmented options={filters} value={filter} onChange={(value) => setFilter(value as ClFilter)} />
      </div>

      <div className="flex flex-col gap-3">
        {rows.map((c) => (
          <ClCampaignRow
            key={c.id}
            campaign={c}
            onToggle={() => toggleActive(c.id)}
            {...(onAdjustBudget ? { onAdjustBudget } : {})}
            {...(onViewLeads ? { onViewLeads } : {})}
            {...(onViewReport ? { onViewReport } : {})}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--cream-2)] pt-4">
        <span className="text-[12px] text-[var(--ink-4)]">
          All metrics are based on verified booked appointments, not ad clicks · ELVA optimizes hourly
        </span>
        <span className="text-[12px] text-[var(--ink-4)]">{rows.length} campaigns shown</span>
      </div>
    </div>
  );
}
